import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Moto, Depoimento, Entrega } from '../types';

interface AppState {
  motos: Moto[];
  depoimentos: Depoimento[];
  entregas: Entrega[];
  isAdmin: boolean;
  setMotos: (motos: Moto[]) => void;
  addMoto: (moto: Moto) => void;
  updateMoto: (id: string, moto: Partial<Moto>) => void;
  deleteMoto: (id: string) => void;
  setDepoimentos: (depoimentos: Depoimento[]) => void;
  addDepoimento: (depoimento: Depoimento) => void;
  setEntregas: (entregas: Entrega[]) => void;
  addEntrega: (entrega: Entrega) => void;
  deleteEntrega: (id: string) => void;
  loginAdmin: () => void;
  logoutAdmin: () => void;
  incrementVisualizacao: (motoId: string) => void;
}

const DESCRICAO_PADRAO = `Pagamento apenas após receber
Frete grátis para todo o Brasil
Garantia Motor 6 Meses
Demais peças 1 ano`;

const MOTOS_INICIAIS: Moto[] = [
  {
    id: 'cg160',
    nome: 'CG 160',
    modelo: 'Fan',
    ano: 2024,
    cilindrada: 160,
    consumo: '41 km/l',
    tanque: '16.1 litros',
    peso: '116 kg',
    preco: 18500,
    precoPromocional: 3699,
    estoque: 5,
    categoria: 'Street',
    descricao: DESCRICAO_PADRAO,
    imagens: ['/img/cg160.jpg'],
    destaque: true,
    visualizacoes: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'biz125',
    nome: 'Biz 125',
    modelo: 'ES',
    ano: 2024,
    cilindrada: 125,
    consumo: '50 km/l',
    tanque: '5.1 litros',
    peso: '100 kg',
    preco: 16800,
    precoPromocional: 2899,
    estoque: 8,
    categoria: 'Street',
    descricao: DESCRICAO_PADRAO,
    imagens: ['/img/biz125.jpg'],
    destaque: true,
    visualizacoes: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'pcx160',
    nome: 'New PCX 160',
    modelo: 'DLX ABS',
    ano: 2024,
    cilindrada: 160,
    consumo: '35 km/l',
    tanque: '8 litros',
    peso: '126 kg',
    preco: 22500,
    precoPromocional: 4199,
    estoque: 3,
    categoria: 'Scooter',
    descricao: DESCRICAO_PADRAO,
    imagens: ['/img/Newpcx160.jpg'],
    destaque: true,
    visualizacoes: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'titan150',
    nome: 'Titan 150',
    modelo: 'ESD',
    ano: 2015,
    cilindrada: 150,
    consumo: '40 km/l',
    tanque: '16.1 litros',
    peso: '117 kg',
    preco: 11500,
    precoPromocional: 2499,
    estoque: 2,
    categoria: 'Street',
    descricao: DESCRICAO_PADRAO,
    imagens: ['/img/titan150.jpg'],
    destaque: false,
    visualizacoes: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'cb250',
    nome: 'CB 250 Twister',
    modelo: 'CBS',
    ano: 2022,
    cilindrada: 250,
    consumo: '30 km/l',
    tanque: '16.5 litros',
    peso: '137 kg',
    preco: 23900,
    precoPromocional: 7499,
    estoque: 4,
    categoria: 'Street',
    descricao: DESCRICAO_PADRAO,
    imagens: ['/img/cb250twister.jpg'],
    destaque: true,
    visualizacoes: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'xre190',
    nome: 'XRE 190',
    modelo: 'Adventure',
    ano: 2024,
    cilindrada: 190,
    consumo: '30 km/l',
    tanque: '13.5 litros',
    peso: '127 kg',
    preco: 24500,
    precoPromocional: 7999,
    estoque: 3,
    categoria: 'Adventure',
    descricao: DESCRICAO_PADRAO,
    imagens: ['/img/xre190.jpg'],
    destaque: true,
    visualizacoes: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'xre300',
    nome: 'XRE 300',
    modelo: 'Rally',
    ano: 2023,
    cilindrada: 300,
    consumo: '25 km/l',
    tanque: '13.8 litros',
    peso: '148 kg',
    preco: 32900,
    precoPromocional: 10899,
    estoque: 2,
    categoria: 'Adventure',
    descricao: DESCRICAO_PADRAO,
    imagens: ['/img/xre300.jpg'],
    destaque: true,
    visualizacoes: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

export const useStore = create<AppState>()(
  persist(
    (set) => ({
      motos: MOTOS_INICIAIS,
      depoimentos: [],
      entregas: [],
      isAdmin: false,
      
      setMotos: (motos) => set({ motos }),
      addMoto: (moto) => set((state) => ({ motos: [...state.motos, moto] })),
      updateMoto: (id, motoAtualizado) => set((state) => ({
        motos: state.motos.map(moto => 
          moto.id === id ? { ...moto, ...motoAtualizado } : moto
        )
      })),
      deleteMoto: (id) => set((state) => ({
        motos: state.motos.filter(moto => moto.id !== id)
      })),
      
      setDepoimentos: (depoimentos) => set({ depoimentos }),
      addDepoimento: (depoimento) => set((state) => ({
        depoimentos: [...state.depoimentos, depoimento]
      })),
      
      setEntregas: (entregas) => set({ entregas }),
      addEntrega: (entrega) => set((state) => ({
        entregas: [...state.entregas, entrega].sort((a, b) => a.ordem - b.ordem)
      })),
      deleteEntrega: (id) => set((state) => ({
        entregas: state.entregas.filter(entrega => entrega.id !== id)
      })),
      
      loginAdmin: () => set({ isAdmin: true }),
      logoutAdmin: () => set({ isAdmin: false }),
      
      incrementVisualizacao: (motoId) => set((state) => ({
        motos: state.motos.map(moto => 
          moto.id === motoId 
            ? { ...moto, visualizacoes: moto.visualizacoes + 1 }
            : moto
        )
      })),
    }),
    {
      name: 'venda-motos-storage', // nome único para o armazenamento
      storage: createJSONStorage(() => localStorage), // usar localStorage
      partialize: (state) => ({ 
        motos: state.motos,
        entregas: state.entregas,
        depoimentos: state.depoimentos,
        isAdmin: state.isAdmin
      }),
      // Forçar atualização dos dados iniciais se estiver vazio (opcional)
      onRehydrateStorage: () => (state) => {
        if (state && state.motos.length === 0) {
          state.setMotos(MOTOS_INICIAIS);
        }
      }
    }
  )
);
